// ─── Écran MENUS : liste des menus-types hebdomadaires ─────────────
// Ouvrir un menu (existant ou nouveau) bascule en page plein écran
// (EcranMenuGrille) — trop riche pour une simple feuille. Embarqué dans
// My Calendar (hub Famille, tuile « Repas ») : `onFermer` revient au hub.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, GesteRetourEcran,
  useUI, useT, useEspace, confirmerSuppression,
} from '@mop/core';
import { ecouterRecettes } from './services/recettes';
import { ecouterRepasComposes } from './services/repasComposes';
import { ecouterRepasConfig, ecouterMenus, creerMenu, supprimerMenu, dupliquerMenu, momentsDuMenu } from './services/menus';
import EcranMenuGrille from './EcranMenuGrille';
import EcranSemaines from './EcranSemaines';

export default function EcranMenus({ calId, onFermer }) {
  const UI = useUI();
  const t = useT();
  const { tenantId, assurerEspace } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [menus, setMenus] = useState([]);
  const [config, setConfig] = useState(null);
  const [recettes, setRecettes] = useState([]);
  const [repasList, setRepasList] = useState([]);
  const [ouvertId, setOuvertId] = useState(null);
  const [vueSemaines, setVueSemaines] = useState(false);
  const [erreur, setErreur] = useState('');
  const signaler = (quoi) => (e) => setErreur(`${quoi} — ${e?.message || e?.code || 'erreur'}`);

  useEffect(() => ecouterMenus(tenantId, (liste) => { setErreur(''); setMenus(liste); }, signaler('menus')), [tenantId]);
  useEffect(() => ecouterRepasConfig(tenantId, setConfig), [tenantId]);
  useEffect(() => ecouterRecettes(tenantId, (liste) => { setErreur(''); setRecettes(liste); }, signaler('recettes')), [tenantId]);
  useEffect(() => ecouterRepasComposes(tenantId, (liste) => { setErreur(''); setRepasList(liste); }, signaler('repas')), [tenantId]);

  const echec = (e) => Alert.alert(t('repas.erreur'), e?.message || '');

  const nouveauMenu = async () => {
    try {
      const espaceId = tenantId || await assurerEspace();
      const id = await creerMenu(espaceId, {});
      setOuvertId(id);
    } catch (e) { echec(e); }
  };

  // Semaines : page plein ecran, comme la grille. Necessite un calendrier —
  // c'est lui qui recevra les repas.
  if (vueSemaines) {
    return (
      <EcranSemaines calId={calId} espaceId={tenantId} menus={menus} config={config} recettes={recettes}
        repasList={repasList} onFermer={() => setVueSemaines(false)} />
    );
  }

  const menuOuvert = menus.find(m => m.id === ouvertId);
  if (ouvertId && menuOuvert) {
    return (
      <EcranMenuGrille espaceId={tenantId} menu={menuOuvert} config={config} recettes={recettes} repasList={repasList}
        onFermer={() => setOuvertId(null)} />
    );
  }

  return (
    <GesteRetourEcran actif={!!onFermer} onRetour={onFermer}>
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      <PageHeader titre={t('repasPlan.titre')} iconeTitre="📆" onRetour={onFermer}
        action={{ icone: '＋', label: t('repasPlan.nouveauMenu'), onPress: nouveauMenu }}>
        {/* Secondaire : les semaines. Rendu en ENFANT du PageHeader (convention
            de l'app : action principale d'abord, puis la grappe). Sans
            calendrier actif, il n'y a nulle part où poser les repas. */}
        {!!calId && (
          <TouchableOpacity onPress={() => setVueSemaines(true)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityLabel={t('repasPlan.semainesTitre')}>
            <Text style={{ fontSize: 20 }}>🗓️</Text>
          </TouchableOpacity>
        )}
      </PageHeader>

      <ScrollView contentContainerStyle={s.corps}>
        {!!erreur && (
          <TouchableOpacity style={s.erreur} activeOpacity={0.8} onPress={() => setErreur('')}>
            <Text style={s.erreurTxt}>⚠️ {erreur}</Text>
          </TouchableOpacity>
        )}

        {menus.length === 0 ? (
          <EmptyState icon="📆" texte={t('repasPlan.aucun')} aide={t('repasPlan.aucunAide')} />
        ) : menus.map(m => {
          const nRepas = Object.keys(m.cases || {}).length;
          return (
            <LigneSwipe key={m.id} espacement={8} enabled
              onEditer={() => setOuvertId(m.id)}
              onDupliquer={() => dupliquerMenu(tenantId, m).catch(echec)}
              onSupprimer={() => confirmerSuppression(t('repasPlan.supprMenuTitre'), m.nom,
                () => supprimerMenu(tenantId, m.id).catch(echec))}>
              <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => setOuvertId(m.id)}>
                <Text style={{ fontSize: 22 }}>📆</Text>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={s.nom} numberOfLines={1}>{m.nom}</Text>
                  <Text style={s.sous} numberOfLines={1}>
                    {t('repasPlan.nRepas', { n: nRepas })} · {momentsDuMenu(m, config).length} plage(s)
                  </Text>
                </View>
              </TouchableOpacity>
            </LigneSwipe>
          );
        })}
      </ScrollView>
    </View>
    </GesteRetourEcran>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13 },
  nom: { fontSize: 15, fontWeight: '800', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 2 },
  erreur: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.danger || '#c0392b', padding: 11, marginBottom: 10 },
  erreurTxt: { fontSize: 13, color: UI.danger || '#c0392b', fontWeight: '700' },
});
